import React, {useRef, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {IconButton, Tooltip, CircularProgress, Box} from '@mui/material';
import {Paperclip} from 'lucide-react';

const FileUploadButton = ({
  onFilesSelected,
  disabled,
  maxFileSize = 50 * 1024 * 1024, // 50MB
  maxTotalSize = 100 * 1024 * 1024, // 100MB
  allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
  ],
  multiple = true,
  isUploading = false,
  uploadedFiles = [],
  onRemoveFile,
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  // Handle file selection via click
  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  // Handle file input change
  const handleFileChange = useCallback(
    (event) => {
      const files = Array.from(event.target.files || []);
      if (files.length > 0) {
        onFilesSelected(files);
      }
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onFilesSelected]
  );

  // Validate files
  const validateFiles = useCallback(
    (files) => {
      const errors = [];
      let totalSize = 0;

      files.forEach((file, index) => {
        // Check file size
        if (file.size > maxFileSize) {
          errors.push(
            `File "${file.name}" is too large. Maximum size is ${Math.round(
              maxFileSize / (1024 * 1024)
            )}MB`
          );
        }

        // Check file type
        if (!allowedTypes.includes(file.type)) {
          errors.push(
            `File "${file.name}" has unsupported type. Allowed types: PDF, JPEG, PNG, WebP`
          );
        }

        totalSize += file.size;
      });

      // Check total size
      if (totalSize > maxTotalSize) {
        errors.push(
          `Total file size too large. Maximum total size is ${Math.round(
            maxTotalSize / (1024 * 1024)
          )}MB`
        );
      }

      return {valid: errors.length === 0, errors};
    },
    [maxFileSize, maxTotalSize, allowedTypes]
  );

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (
        !disabled &&
        e.dataTransfer.items &&
        e.dataTransfer.items.length > 0
      ) {
        setDragActive(true);
      }
    },
    [disabled]
  );

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (disabled) return;

      const files = [];
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        for (let i = 0; i < e.dataTransfer.files.length; i++) {
          files.push(e.dataTransfer.files[i]);
        }

        const {valid, errors} = validateFiles(files);
        if (valid) {
          onFilesSelected(files);
        } else {
          console.error('File validation errors:', errors);
          // You might want to show these errors to the user
        }
      }
    },
    [disabled, onFilesSelected, validateFiles]
  );

  // Handle paste events (for pasting images)
  const handlePaste = useCallback(
    (e) => {
      if (disabled) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        const {valid, errors} = validateFiles(files);
        if (valid) {
          onFilesSelected(files);
        } else {
          console.error('File validation errors:', errors);
        }
      }
    },
    [disabled, onFilesSelected, validateFiles]
  );

  // Set up global paste listener
  React.useEffect(() => {
    const handleGlobalPaste = (e) => {
      // Only handle paste if the target is within our component or is a text input
      const target = e.target;
      if (
        target.closest('.agent-chat-drawer') ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true'
      ) {
        handlePaste(e);
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [handlePaste]);

  // Generate accepted file types for input
  const acceptedTypes = allowedTypes.join(',');

  return (
    <Box
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        ...(dragActive && {
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 100, 0, 0.1)',
            border: '2px dashed #4caf50',
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 1000,
          },
        }),
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={acceptedTypes}
        onChange={handleFileChange}
        style={{display: 'none'}}
      />

      <Tooltip
        title={
          dragActive
            ? 'Drop files here'
            : 'Attach files (click, drag & drop, or paste images)'
        }
      >
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled || isUploading}
            size="small"
            sx={{
              color: dragActive ? '#4caf50' : 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {isUploading ? (
              <CircularProgress size={16} />
            ) : (
              <Paperclip
                size={16}
                style={{
                  opacity: disabled ? 0.5 : dragActive ? 1 : 0.7,
                }}
              />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

FileUploadButton.propTypes = {
  onFilesSelected: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  maxFileSize: PropTypes.number,
  maxTotalSize: PropTypes.number,
  allowedTypes: PropTypes.arrayOf(PropTypes.string),
  multiple: PropTypes.bool,
  isUploading: PropTypes.bool,
  uploadedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
    })
  ),
  onRemoveFile: PropTypes.func,
};

export default FileUploadButton;
